import React, { useState, useEffect } from 'react';
import { Button, Badge, Input, Modal, Loader, Select, Alert } from './shared';
import Table, { TableHead, TableBody, TableRow, TableCell } from './shared/Table';

const DNSManager = ({ service, isOnline }) => {
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateRecord, setShowCreateRecord] = useState(false);
  const [showEditRecord, setShowEditRecord] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [filterType, setFilterType] = useState('');

  // New record form
  const [newRecord, setNewRecord] = useState({
    domain_name: '',
    name: '',
    type: 'A',
    content: '',
    ttl: 3600,
    priority: ''
  });

  const recordTypes = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'SRV', 'NS', 'CAA', 'ALIAS'];

  // Get auth token - Use service API key
  const getAuthToken = () => {
    // DNS Manager uses the service's built-in API key
    return service.apiKey || sessionStorage.getItem('authToken');
  };

  // Load domains
  const loadDomains = async () => {
    if (!isOnline) return;

    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${service.url}/domains`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDomains(data.domains || []);
        if (data.domains && data.domains.length > 0 && !selectedDomain) {
          setSelectedDomain(data.domains[0].name);
        }
      } else if (response.status === 401) {
        setError('Authentication required. Please log in again.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to load domains');
      }
    } catch (err) {
      console.error('Failed to load domains:', err);
      setError('Failed to connect to DNS service');
    } finally {
      setLoading(false);
    }
  };

  // Load DNS records for selected domain
  const loadRecords = async (domainName = selectedDomain) => {
    if (!isOnline || !domainName) {
      console.log('DNS Manager: Skipping loadRecords - isOnline:', isOnline, 'domainName:', domainName);
      return;
    }

    console.log('DNS Manager: Loading records for domain:', domainName);
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();

      // Build URL with query parameters
      let url = `${service.url}/domains/${domainName}/records`;
      if (filterType) {
        url += `?type=${filterType}`;
      }

      console.log('DNS Manager: Fetching from URL:', url);
      console.log('DNS Manager: Using token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('DNS Manager: Response status:', response.status, 'OK:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('DNS Manager: Received data:', {
          success: data.success,
          count: data.count,
          recordsLength: data.records?.length || 0
        });
        setRecords(data.records || []);
      } else if (response.status === 401) {
        setError('Authentication required. Please log in again.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to load DNS records');
      }
    } catch (err) {
      console.error('DNS Manager: Failed to load records:', err);
      setError('Failed to connect to DNS service');
    } finally {
      setLoading(false);
    }
  };

  // Create DNS record
  const createRecord = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const payload = {
        domain_name: selectedDomain,
        name: newRecord.name,
        type: newRecord.type,
        content: newRecord.content,
        ttl: parseInt(newRecord.ttl)
      };

      // Add priority for MX and SRV records
      if ((newRecord.type === 'MX' || newRecord.type === 'SRV') && newRecord.priority) {
        payload.priority = parseInt(newRecord.priority);
      }

      const response = await fetch(`${service.url}/records`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowCreateRecord(false);
        setNewRecord({
          domain_name: '',
          name: '',
          type: 'A',
          content: '',
          ttl: 3600,
          priority: ''
        });
        loadRecords();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to create DNS record');
      }
    } catch (err) {
      console.error('Failed to create record:', err);
      setError('Failed to create DNS record');
    } finally {
      setLoading(false);
    }
  };

  // Update DNS record
  const updateRecord = async () => {
    if (!editingRecord) return;

    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const payload = {};

      if (editingRecord.name !== editingRecord.originalName) {
        payload.name = editingRecord.name;
      }
      if (editingRecord.content !== editingRecord.originalContent) {
        payload.content = editingRecord.content;
      }
      if (editingRecord.ttl !== editingRecord.originalTtl) {
        payload.ttl = parseInt(editingRecord.ttl);
      }
      if (editingRecord.priority && editingRecord.priority !== editingRecord.originalPriority) {
        payload.priority = parseInt(editingRecord.priority);
      }

      const response = await fetch(`${service.url}/domains/${selectedDomain}/records/${editingRecord.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowEditRecord(false);
        setEditingRecord(null);
        loadRecords();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to update DNS record');
      }
    } catch (err) {
      console.error('Failed to update record:', err);
      setError('Failed to update DNS record');
    } finally {
      setLoading(false);
    }
  };

  // Delete DNS record
  const deleteRecord = async () => {
    if (!recordToDelete) return;

    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${service.url}/domains/${selectedDomain}/records/${recordToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setShowDeleteConfirm(false);
        setRecordToDelete(null);
        loadRecords();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to delete DNS record');
      }
    } catch (err) {
      console.error('Failed to delete record:', err);
      setError('Failed to delete DNS record');
    } finally {
      setLoading(false);
    }
  };

  // Load domains on mount
  useEffect(() => {
    loadDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  // Load records when domain or filter changes
  useEffect(() => {
    if (selectedDomain) {
      loadRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain, filterType]);

  const handleEditClick = (record) => {
    setEditingRecord({
      ...record,
      originalName: record.name,
      originalContent: record.content,
      originalTtl: record.ttl,
      originalPriority: record.priority
    });
    setShowEditRecord(true);
  };

  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setShowDeleteConfirm(true);
  };

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-4 w-full sm:w-auto">
          <Select
            value={selectedDomain || ''}
            onChange={(e) => setSelectedDomain(e.target.value)}
            disabled={loading || domains.length === 0}
            className="flex-1 sm:w-64"
          >
            <option value="">Select a domain</option>
            {domains.map((domain) => (
              <option key={domain.id} value={domain.name}>
                {domain.name}
              </option>
            ))}
          </Select>
          <Button
            onClick={loadRecords}
            variant="secondary"
            disabled={loading || !selectedDomain}
          >
            <i className="fas fa-sync-alt mr-2"></i>
            Refresh
          </Button>
        </div>

        <Button
          onClick={() => setShowCreateRecord(true)}
          disabled={!selectedDomain}
        >
          <i className="fas fa-plus mr-2"></i>
          Add Record
        </Button>
      </div>

      {/* Filter by Record Type */}
      {selectedDomain && (
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">Filter by type:</label>
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-32"
          >
            <option value="">All</option>
            {recordTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </Select>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Domain Info */}
      {selectedDomain && domains.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <i className="fas fa-globe mr-2 text-blue-600"></i>
              {selectedDomain}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {domains.filter(d => d.name === selectedDomain).map(domain => (
              <React.Fragment key={domain.id}>
                <div>
                  <p className="text-xs text-gray-500">Domain ID</p>
                  <p className="text-sm font-medium text-gray-900">{domain.id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">State</p>
                  <Badge variant={domain.state === 'registered' ? 'success' : 'warning'}>
                    {domain.state || 'Unknown'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Auto Renew</p>
                  <p className="text-sm font-medium text-gray-900">
                    {domain.auto_renew ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Records</p>
                  <p className="text-sm font-medium text-gray-900">{records.length}</p>
                </div>
                <div className="col-span-full pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-500">Expires</p>
                  <p className="text-sm font-medium text-gray-900">
                    {domain.expires_on ? new Date(domain.expires_on).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* DNS Records Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader size="large" />
        </div>
      ) : records.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Content
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  TTL
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>
                    <div className="flex items-center">
                      <span className="font-medium text-gray-900">
                        {record.name || '@'}
                      </span>
                      {record.system_record && (
                        <Badge variant="secondary" className="ml-2 text-xs">System</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="info">{record.type}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600 break-all">
                      {record.content}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">{record.ttl}s</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">
                      {record.priority || '-'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEditClick(record)}
                        disabled={record.system_record}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteClick(record)}
                        disabled={record.system_record}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : selectedDomain ? (
        <div className="text-center py-12">
          <i className="fas fa-globe text-4xl text-gray-300 mb-4"></i>
          <p className="text-gray-500">No DNS records found for this domain</p>
          <Button
            onClick={() => setShowCreateRecord(true)}
            className="mt-4"
          >
            <i className="fas fa-plus mr-2"></i>
            Add First Record
          </Button>
        </div>
      ) : (
        <div className="text-center py-12">
          <i className="fas fa-globe text-4xl text-gray-300 mb-4"></i>
          <p className="text-gray-500">Select a domain to view DNS records</p>
        </div>
      )}

      {/* Create Record Modal */}
      <Modal
        isOpen={showCreateRecord}
        onClose={() => setShowCreateRecord(false)}
        title="Create DNS Record"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domain
            </label>
            <Input
              value={selectedDomain || ''}
              disabled
              className="bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Record Name <span className="text-xs text-gray-500">(use @ for root domain)</span>
            </label>
            <Input
              value={newRecord.name}
              onChange={(e) => setNewRecord({ ...newRecord, name: e.target.value })}
              placeholder="www"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Record Type
            </label>
            <Select
              value={newRecord.type}
              onChange={(e) => setNewRecord({ ...newRecord, type: e.target.value })}
            >
              {recordTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content
            </label>
            <Input
              value={newRecord.content}
              onChange={(e) => setNewRecord({ ...newRecord, content: e.target.value })}
              placeholder={
                newRecord.type === 'A' ? '192.168.1.1' :
                newRecord.type === 'CNAME' ? 'example.com' :
                newRecord.type === 'MX' ? 'mail.example.com' :
                'Value'
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                TTL (seconds)
              </label>
              <Input
                type="number"
                value={newRecord.ttl}
                onChange={(e) => setNewRecord({ ...newRecord, ttl: e.target.value })}
                min="300"
                max="86400"
              />
            </div>

            {(newRecord.type === 'MX' || newRecord.type === 'SRV') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <Input
                  type="number"
                  value={newRecord.priority}
                  onChange={(e) => setNewRecord({ ...newRecord, priority: e.target.value })}
                  placeholder="10"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowCreateRecord(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={createRecord}
              disabled={!newRecord.name || !newRecord.content || loading}
            >
              {loading ? 'Creating...' : 'Create Record'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Record Modal */}
      <Modal
        isOpen={showEditRecord}
        onClose={() => setShowEditRecord(false)}
        title="Edit DNS Record"
      >
        {editingRecord && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Record Name
              </label>
              <Input
                value={editingRecord.name}
                onChange={(e) => setEditingRecord({ ...editingRecord, name: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <Input value={editingRecord.type} disabled className="bg-gray-50" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content
              </label>
              <Input
                value={editingRecord.content}
                onChange={(e) => setEditingRecord({ ...editingRecord, content: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  TTL (seconds)
                </label>
                <Input
                  type="number"
                  value={editingRecord.ttl}
                  onChange={(e) => setEditingRecord({ ...editingRecord, ttl: e.target.value })}
                  min="300"
                  max="86400"
                />
              </div>

              {(editingRecord.type === 'MX' || editingRecord.type === 'SRV') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <Input
                    type="number"
                    value={editingRecord.priority || ''}
                    onChange={(e) => setEditingRecord({ ...editingRecord, priority: e.target.value })}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowEditRecord(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={updateRecord}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Record'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete DNS Record"
      >
        {recordToDelete && (
          <div className="space-y-4">
            <Alert variant="warning">
              Are you sure you want to delete this DNS record? This action cannot be undone.
            </Alert>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-500">Name:</div>
                <div className="font-medium">{recordToDelete.name || '@'}</div>
                <div className="text-gray-500">Type:</div>
                <div className="font-medium">{recordToDelete.type}</div>
                <div className="text-gray-500">Content:</div>
                <div className="font-medium break-all">{recordToDelete.content}</div>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={deleteRecord}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete Record'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DNSManager;
